<template>
  <div class="app" :data-theme="theme">
    <a class="skip-link" href="#chat-panel">Skip to chat</a>

    <header class="site-header glass" aria-label="Display settings">
      <div class="site-header__inner">
        <button type="button" class="btn-icon" :aria-pressed="theme === 'dark'" aria-label="Toggle dark mode" @click="toggleTheme">
          <span v-if="theme === 'light'" aria-hidden="true">🌙</span>
          <span v-else aria-hidden="true">☀️</span>
        </button>
      </div>
    </header>

    <main class="main">
      <section id="chat-panel" class="chat-section" tabindex="-1" aria-labelledby="chat-heading">
        <div class="chat-shell glass">
          <div class="chat-shell__head">
            <div>
              <h1 id="chat-heading" class="chat-shell__title">Happy Chat</h1>
              <p class="chat-shell__sub">A warm companion — demo, not professional care.</p>
            </div>
            <button type="button" class="btn btn--text" :disabled="messages.length === 0 || isLoading" @click="clearChat">
              Clear
            </button>
          </div>

          <div
            ref="scrollRef"
            class="chat-scroll"
            role="log"
            aria-live="polite"
            aria-relevant="additions"
          >
            <div v-if="messages.length === 0 && !isLoading" class="empty-state">
              <img src="./assets/robot1.png" alt="" class="empty-state__art" width="120" height="120" />
              <p class="empty-state__title">Hi, I am Happy Chat</p>
              <p class="empty-state__text">Share what is on your mind — a win, a worry, or something in between. I will respond with warmth and clarity.</p>
            </div>

            <ul v-else class="messages">
              <li
                v-for="m in messages"
                :key="m.id"
                class="msg-row"
                :class="'msg-row--' + m.role"
              >
                <div class="bubble" :class="'bubble--' + m.role">
                  <span class="bubble__label">{{ m.role === 'user' ? 'You' : 'Happy Chat' }}</span>
                  <div
                    v-if="m.role === 'assistant'"
                    class="bubble__body markdown-body"
                    v-html="rendered(m.content)"
                  />
                  <p v-else class="bubble__body">{{ m.content }}</p>
                </div>
              </li>
            </ul>

            <div v-if="isLoading" class="typing-block" aria-hidden="true">
              <div v-if="messages.length === 0" class="skeleton" aria-label="Loading">
                <div class="skeleton-line" />
                <div class="skeleton-line skeleton-line--short" />
              </div>
              <div class="typing" role="status">
                <span class="typing__dot" />
                <span class="typing__dot" />
                <span class="typing__dot" />
              </div>
            </div>
          </div>

          <p v-if="formError" class="field-error" role="alert">{{ formError }}</p>

          <form class="composer" @submit.prevent="send">
            <label class="visually-hidden" for="composer-input">Your message</label>
            <textarea
              id="composer-input"
              ref="inputRef"
              v-model="draft"
              class="composer__input"
              rows="1"
              maxlength="4000"
              placeholder="Write your message…"
              :disabled="isLoading"
              @keydown.enter.exact.prevent="onEnter"
            />
            <button type="submit" class="btn btn--primary composer__send" :disabled="isLoading || !draft.trim()">
              Send
            </button>
          </form>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import { renderMarkdown } from './utils/markdown';

const THEME_KEY = 'happy-chat-theme';

const theme = ref('light');
const draft = ref('');
const messages = ref([]);
const isLoading = ref(false);
const formError = ref('');
const scrollRef = ref(null);
const inputRef = ref(null);

const SYSTEM_PROMPT =
  'You are a friendly and supportive AI companion. Respond in a warm, helpful, and encouraging way. Keep responses conversational and positive. Use markdown when it improves clarity (headings, short lists, bold for emphasis).';

function uid() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function rendered(text) {
  return renderMarkdown(text);
}

function applyTheme(t) {
  theme.value = t;
  document.documentElement.dataset.theme = t;
  try {
    localStorage.setItem(THEME_KEY, t);
  } catch {
    /* ignore */
  }
}

function toggleTheme() {
  applyTheme(theme.value === 'light' ? 'dark' : 'light');
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

async function scrollToBottom() {
  await nextTick();
  const el = scrollRef.value;
  if (!el) return;
  el.scrollTo({ top: el.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

watch(messages, scrollToBottom, { deep: true });
watch(isLoading, (v) => {
  if (v) scrollToBottom();
});

onMounted(() => {
  let initial = 'light';
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') initial = saved;
    else if (window.matchMedia('(prefers-color-scheme: dark)').matches) initial = 'dark';
  } catch {
    /* ignore */
  }
  applyTheme(initial);
});

function clearChat() {
  messages.value = [];
  formError.value = '';
}

function onEnter() {
  send();
}

async function send() {
  const text = draft.value.trim();
  if (!text) {
    formError.value = 'Please add a message before sending.';
    return;
  }
  formError.value = '';
  const userMsg = { id: uid(), role: 'user', content: text };
  messages.value = [...messages.value, userMsg];
  draft.value = '';
  isLoading.value = true;

  const history = [
    { role: 'system', content: SYSTEM_PROMPT },
    ...messages.value.map((m) => ({ role: m.role, content: m.content })),
  ];

  try {
    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: history,
        max_tokens: 512,
        temperature: 0.7,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = typeof data.error === 'string' ? data.error : 'Something went wrong.';
      messages.value = [
        ...messages.value,
        { id: uid(), role: 'assistant', content: `I could not complete that request: ${err}` },
      ];
      return;
    }
    if (typeof data.content !== 'string') {
      messages.value = [
        ...messages.value,
        { id: uid(), role: 'assistant', content: 'I received an unexpected response. Please try again.' },
      ];
      return;
    }
    messages.value = [...messages.value, { id: uid(), role: 'assistant', content: data.content }];
  } catch (e) {
    messages.value = [
      ...messages.value,
      {
        id: uid(),
        role: 'assistant',
        content: `Network error: ${e && e.message ? e.message : 'Please check your connection and try again.'}`,
      },
    ];
  } finally {
    isLoading.value = false;
    await nextTick();
    if (inputRef.value) inputRef.value.focus();
  }
}
</script>

<style>
:root,
[data-theme='light'] {
  --bg0: #faf7f4;
  --bg1: #f3ebe4;
  --text: #1e293b;
  --muted: #64748b;
  --accent: #c2410c;
  --accent2: #ea580c;
  --card: rgba(255, 255, 255, 0.72);
  --stroke: rgba(15, 23, 42, 0.08);
  --shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
  --bubble-user: linear-gradient(135deg, #fff7ed, #ffedd5);
  --bubble-ai: rgba(255, 255, 255, 0.92);
}

[data-theme='dark'] {
  --bg0: #0f172a;
  --bg1: #1e293b;
  --text: #f1f5f9;
  --muted: #94a3b8;
  --accent: #fb923c;
  --accent2: #fdba74;
  --card: rgba(30, 41, 59, 0.75);
  --stroke: rgba(248, 250, 252, 0.08);
  --shadow: 0 22px 60px rgba(0, 0, 0, 0.45);
  --bubble-user: linear-gradient(135deg, #431407, #7c2d12);
  --bubble-ai: rgba(15, 23, 42, 0.85);
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(251, 146, 60, 0.18), transparent 55%),
    radial-gradient(900px 500px at 90% 0%, rgba(147, 197, 253, 0.15), transparent 50%),
    linear-gradient(180deg, var(--bg0), var(--bg1));
  transition: background 0.35s ease, color 0.25s ease;
}

.skip-link {
  position: absolute;
  left: -999px;
  top: 0;
  padding: 0.5rem 1rem;
  background: var(--accent);
  color: #fff;
  z-index: 100;
  border-radius: 0 0 8px 0;
}
.skip-link:focus {
  left: 0;
}

.glass {
  background: var(--card);
  border: 1px solid var(--stroke);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
}

.site-header {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 20;
  border-bottom: 1px solid var(--stroke);
}

.site-header__inner {
  max-width: min(960px, 100%);
  margin: 0 auto;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.btn-icon {
  border: 1px solid var(--stroke);
  background: var(--card);
  border-radius: 999px;
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}
.btn-icon:hover {
  transform: translateY(-1px);
}
.btn-icon:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  max-width: min(960px, 100%);
  margin: 0 auto;
  padding: 0.65rem 1rem 0.75rem;
  box-sizing: border-box;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.65rem 1.35rem;
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn--primary {
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  color: #fff;
  box-shadow: 0 10px 30px rgba(234, 88, 12, 0.25);
}
.btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn--text {
  background: none;
  border: none;
  color: var(--muted);
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem 0.5rem;
}
.btn--text:hover:not(:disabled) {
  color: var(--accent);
}

.chat-section {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 0;
}

.chat-shell {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 20px;
  overflow: hidden;
  width: 100%;
  margin: 0 auto;
}

.chat-shell__head {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.1rem;
  border-bottom: 1px solid var(--stroke);
}

.chat-shell__title {
  margin: 0;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.25;
}
.chat-shell__sub {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.45;
  max-width: 28rem;
}

.chat-scroll {
  flex: 1 1 auto;
  min-height: 12rem;
  overflow-y: auto;
  padding: 1rem 1.1rem;
  scroll-behavior: smooth;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem 1rem;
}
.empty-state__art {
  opacity: 0.9;
  filter: drop-shadow(0 12px 24px rgba(15, 23, 42, 0.12));
}
.empty-state__title {
  font-weight: 700;
  font-size: 1.15rem;
  margin: 0.75rem 0 0.35rem;
}
.empty-state__text {
  margin: 0 auto;
  max-width: 22rem;
  color: var(--muted);
  line-height: 1.6;
  font-size: 0.95rem;
}

.messages {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.msg-row {
  display: flex;
}
.msg-row--user {
  justify-content: flex-end;
}
.msg-row--assistant {
  justify-content: flex-start;
}

.bubble {
  max-width: min(100%, 34rem);
  border-radius: 18px;
  padding: 0.65rem 0.9rem 0.85rem;
  border: 1px solid var(--stroke);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}
.bubble--user {
  background: var(--bubble-user);
}
.bubble--assistant {
  background: var(--bubble-ai);
}

.bubble__label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
  margin-bottom: 0.25rem;
}

.bubble__body {
  margin: 0;
  line-height: 1.55;
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.markdown-body :deep(p) {
  margin: 0.35rem 0;
}
.markdown-body :deep(p:first-child) {
  margin-top: 0;
}
.markdown-body :deep(p:last-child) {
  margin-bottom: 0;
}
.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  margin: 0.35rem 0 0.35rem 1.1rem;
  padding: 0;
}
.markdown-body :deep(a) {
  color: var(--accent);
}
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3) {
  margin: 0.5rem 0 0.25rem;
  font-size: 1rem;
  line-height: 1.3;
}
.markdown-body :deep(.hljs-wrap) {
  margin: 0.5rem 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--stroke);
}
.markdown-body :deep(pre.hljs-wrap) {
  background: #0d1117;
}
.markdown-body :deep(.hljs) {
  background: transparent !important;
}

.typing-block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-width: 14rem;
}
.skeleton-line {
  height: 0.65rem;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.15),
    rgba(148, 163, 184, 0.35),
    rgba(148, 163, 184, 0.15)
  );
  background-size: 200% 100%;
  animation: shimmer 1.1s ease-in-out infinite;
}
.skeleton-line--short {
  width: 55%;
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.typing {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.45rem 0.65rem;
  border-radius: 999px;
  background: var(--bubble-ai);
  border: 1px solid var(--stroke);
  width: fit-content;
}
.typing__dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--muted);
  opacity: 0.35;
  animation: bounce 1.1s infinite ease-in-out;
}
.typing__dot:nth-child(2) {
  animation-delay: 0.12s;
}
.typing__dot:nth-child(3) {
  animation-delay: 0.24s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.35;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.field-error {
  color: #b91c1c;
  font-size: 0.85rem;
  margin: 0.35rem 1.25rem 0;
}

.composer {
  flex-shrink: 0;
  display: flex;
  gap: 0.6rem;
  align-items: flex-end;
  padding: 0.65rem 1rem 0.85rem;
  border-top: 1px solid var(--stroke);
  background: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .composer {
  background: rgba(15, 23, 42, 0.35);
}

.composer__input {
  flex: 1;
  resize: none;
  min-height: 2.75rem;
  max-height: 8rem;
  border-radius: 14px;
  border: 1px solid var(--stroke);
  padding: 0.65rem 0.85rem;
  font: inherit;
  color: inherit;
  background: var(--card);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.composer__input:focus {
  outline: none;
  border-color: rgba(234, 88, 12, 0.45);
  box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.15);
}
.composer__input:disabled {
  opacity: 0.65;
}

.composer__send {
  flex-shrink: 0;
  border: none;
  white-space: nowrap;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 640px) {
  .chat-shell__head {
    flex-wrap: wrap;
  }
}
</style>
